<template>
    <asli-form-section :title="`Thank you ${personalDetails?.first_name}!`" heading-level="h2">
        <div class="flex flex-col gap-[15px]">
            <div class="bg-lighterGreen flex flex-col gap-2.5 p-[30px] rounded-md">
                <p class="text-lg font-bold">Your payment of €{{ paymentAmount }} has been received</p>
                <p class="text-sm">
                    You will shortly receive an email to {{ risk.proposer.email }} containing details of your new policy
                    documentation. This email will outline any actions you need to take.
                </p>
            </div>
            <div>
                <p class="font-bold text-lg mb-4">Here’s your policy reference number: {{ quote?.policy_number }}</p>
            </div>
        </div>
        <div class="flex flex-col md:flex-row w-full gap-10 md:gap-0 mb-[35px]">
            <div class="basis-full md:basis-1/2 flex flex-col md:flex-row">
                <div class="basis-full">
                    <p class="font-bold text-lg mb-4">Policy Details</p>
                    <p class="mb-4">
                        Cover Type: <span class="font-bold">{{ coverType }}</span>
                    </p>
                    <p class="mb-4">
                        Insurer Details: <span class="font-bold">{{ quote?.insurer_name }}</span>
                    </p>
                    <p class="mb-4">
                        Total Payable:
                        <span class="font-bold">€{{ isPayMonthly ? monthlyTotalPayable : premium }}</span>
                    </p>
                    <p class="mb-4">
                        Cover Effective From:
                        <span class="font-bold"
                            >{{ formattedEffectiveFrom }}
                            <span v-if="isEffectiveFromToday" class="relative text-xs -top-0.5">From Now</span>
                        </span>
                    </p>
                    <p>
                        Cover Effective To:
                        <span class="font-bold">{{ formattedEffectiveTo }}</span>
                    </p>
                </div>
            </div>
            <div class="basis-full md:basis-1/2">
                <p class="font-bold text-lg mb-[12px]">Your Address</p>
                <p class="mb-2">
                    <span v-if="addressDetails?.address?.address_line1"
                        >{{ addressDetails?.address?.address_line1 }},
                    </span>
                    <span v-if="addressDetails?.address?.address_line2"
                        >{{ addressDetails?.address?.address_line2 }},
                    </span>
                    <span v-if="addressDetails?.address?.address_line3"
                        >{{ addressDetails?.address?.address_line3 }},
                    </span>
                    <span v-if="addressDetails?.address?.address_line4">{{
                        addressDetails?.address?.address_line4
                    }}</span>
                </p>
                <div class="flex flex-row mb-[12px]">
                    <asli-address-search-display :eircode="addressDetails?.address.eircode" />
                </div>
                <div class="flex flex-row mr-4 text-center items-center gap-5">
                    <asli-details-box-stat icon="calendarBlack" aria-label="Year property built">
                        {{ addressDetails?.year_built?.slice(0, 4) }}
                    </asli-details-box-stat>
                    <asli-details-box-stat icon="bed" aria-label="Bedrooms">
                        {{ addressDetails?.number_of_bedrooms }}
                    </asli-details-box-stat>
                    <asli-details-box-stat icon="bathroom" aria-label="Bathrooms">
                        {{ addressDetails?.number_of_bathrooms }}
                    </asli-details-box-stat>
                    <asli-details-box-stat icon="fire" class="min-w-fit" aria-label="Heating type">
                        {{ getHeatingSystemDisplay(addressDetails?.heating_system ?? "") }}
                    </asli-details-box-stat>
                </div>
            </div>
        </div>
    </asli-form-section>
    <button-bar class="flex-col items-center md:items-end">
        <asli-button id="thankyou-back" color="primary" type="button" @click="goToHomepage">Go to Homepage</asli-button>
    </button-bar>
</template>

<script lang="ts" setup>
import { useMainStore } from "@/stores/main";
import {
    AsliFormSection,
    AsliButton,
    appendDecimal,
    isToday,
    AsliAddressSearchDisplay,
    getHeatingSystemDisplay,
    AsliDetailsBoxStat
} from "@mbs-npm/anpost-ui";
import ButtonBar from "@/components/ButtonBar.vue";
import { useProposalStore } from "@/stores/risk/risk";
import { computed, onMounted } from "vue";
import { formatDate, formatDateAndTime } from "@/utils";
import { AsliIcon } from "@mbs-npm/anpost-ui";
import { BRAND_HOME_PAGE } from "@/static/constants";
import { useQuoteForDisplay } from "@/hooks/useQuoteForDisplay";
import { APP_STEP } from "@/stores/types";

const mainStore = useMainStore();
const proposalStore = useProposalStore();

mainStore.setAppStep(APP_STEP.THANK_YOU);

const proposal = computed(() => proposalStore?.proposal);
const risk = computed(() => proposal.value?.risk);
const quote = computed(() => proposal.value?.quote);

const mappedQuote = computed(() => useQuoteForDisplay(quote.value));
const coverType = computed(() => mappedQuote.value.coverLevel);

const personalDetails = computed(() => risk.value?.proposer?.personal_details);
const addressDetails = computed(() => risk.value?.property_details);

const isPayMonthly = computed(() => proposal.value?.is_pay_monthly);

const premium = computed(() => appendDecimal(quote.value?.premium?.toString(), true));
const monthlyTotalPayable = computed(() => appendDecimal(quote.value?.monthly_total_payable?.toString(), true));

const effectiveFrom = computed(() => quote.value?.cover_effective_from);
const isEffectiveFromToday = isToday(effectiveFrom.value);
const formattedEffectiveFrom = isEffectiveFromToday
    ? formatDate(effectiveFrom.value)
    : formatDateAndTime(effectiveFrom.value);
const formattedEffectiveTo = formatDateAndTime(quote.value?.cover_effective_to);

const paymentAmount = computed(() => appendDecimal(quote.value?.payment?.amount?.toString(), true));

function goToHomepage() {
    window.location.href = BRAND_HOME_PAGE;
}

// QUOTE WRAP UP / ACCEPTING A QUOTE

onMounted(() => {
    if (proposal.value?.is_purchased && !proposal.value?.is_accepted && proposal.value?.uuid) {
        proposalStore?.quoteWrapUp(proposal.value?.uuid);
    }
});
</script>
